import SectionHeader from "../components/SectionHeader";
import LogDriveDiagram from "../components/LogDriveDiagram";
import ConfigurationSchematic from "../components/ConfigurationSchematic";
import SEO from "../components/SEO";
import { TECH_PILLARS, HERO_STATS } from "../lib/content";

export default function TechnologyPage() {
  return (
    <main data-testid="page-technology">
      <SEO
        path="/technology"
        image="/brand/og-technology.jpg"
        title="Technology"
        description="LogDrive — hollow-shaft, bearing-ball speed reducer. 1:1 to 225:1 ratios, 75–95% efficiency, near-zero backlash. Step-function torque and power density vs. harmonic, RV / cycloidal and planetary reducers."
      />
      {/* HERO */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-20 border-b border-white/5">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7 reveal">
            <div className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-white/50">
              / technology · deep dive
            </div>
            <h1 className="font-display font-black text-5xl sm:text-6xl md:text-8xl leading-[0.9] tracking-tighter mt-6">
              A bank of bearings.
              <br />
              A wedge principle.
              <br />
              <span className="text-[#FF3B30]">A reducer reimagined.</span>
            </h1>
          </div>
          <div className="md:col-span-5">
            <p className="text-white/70 leading-relaxed">
              The Logarithmic Drive replaces meshing gear teeth with a
              simultaneously-loaded bank of bearing balls, constrained between
              two wedges of differing angles. Every ball carries load at once.
              The result is a compact, coaxial reducer with high torque density
              and a hollow shaft — something conventional planetary and cycloidal
              drives can't deliver in the same envelope.
            </p>
          </div>
        </div>
      </section>

      {/* SPECS */}
      <section className="relative py-16 bg-[#0a0a0a] border-b border-white/5" data-testid="tech-specs">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {HERO_STATS.map((s) => (
            <div key={s.k} className="border-l border-white/10 pl-5">
              <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">
                {s.k}
              </div>
              <div className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-2">
                {s.v}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PRINCIPLE */}
      <section className="relative py-24 md:py-32" data-testid="tech-principle">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5 md:sticky md:top-24">
            <SectionHeader
              kicker="/ principle"
              title={<>The wedge,<br />amplified.</>}
              lead="A low-angle input wedge pushes a ball upward. The ball then loads a
high-angle output wedge. Force is amplified; horizontal travel is reduced.
Repeat that interaction across a full ring of bearing balls, in continuous
rotary motion, and you get a clean logarithmic reduction."
            />
            <ul className="mt-8 space-y-4">
              {TECH_PILLARS.map((p) => (
                <li key={p.id} className="border border-white/10 p-5">
                  <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">
                    {p.label}
                  </div>
                  <p className="mt-2 text-white/80 leading-relaxed">{p.body}</p>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-7">
            <div className="diagram-grid border border-white/10 p-6">
              <LogDriveDiagram />
            </div>
            <div className="mt-8 grid grid-cols-2 gap-4">
              {[
                { k: "Components", v: "3 · I / M / O" },
                { k: "Shaft", v: "Hollow · coaxial" },
                { k: "Load share", v: "All balls simultaneously" },
                { k: "Failure mode", v: "Graceful · distributed" },
              ].map((x) => (
                <div key={x.k} className="border border-white/10 p-4">
                  <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">
                    {x.k}
                  </div>
                  <div className="mt-1 font-display text-lg font-bold">{x.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WEDGE PRINCIPLE — VIDEO */}
      <section className="relative py-24 md:py-32 border-y border-white/5" data-testid="tech-wedge">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-7">
            <div className="border border-white/10 bg-black overflow-hidden">
              <div className="px-4 py-3 flex items-center justify-between border-b border-white/10 bg-[#0a0a0a]">
                <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/60">
                  / functioning concept
                </div>
                <div className="flex items-center gap-1">
                  <span className="h-2 w-2 bg-[#FF3B30]" />
                  <span className="font-mono-tech text-[10px] uppercase tracking-widest text-white/60">
                    rec
                  </span>
                </div>
              </div>
              <video
                src="/media/wedge-principle.mp4"
                className="block w-full h-auto bg-black"
                autoPlay
                loop
                muted
                playsInline
                controls
                preload="metadata"
                data-testid="wedge-video"
              />
            </div>
          </div>

          <div className="md:col-span-5">
            <div className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-white/50">
              / wedge principle
            </div>
            <h2 className="mt-4 font-display font-black text-4xl md:text-6xl tracking-tighter leading-[0.95]">
              Two wedges.
              <br />
              One ball.
              <br />
              <span className="text-[#FF3B30]">Reduction.</span>
            </h2>

            <p className="mt-6 text-white/75 leading-relaxed">
              A wedge converts a small linear input motion into a large normal
              force, in proportion to its angle. LogDrive uses this directly:
              two wedge surfaces — one shallow, one steep — separated by a
              bearing ball captive in a radial slot.
            </p>
            <p className="mt-4 text-white/75 leading-relaxed">
              As the input wedge rotates, its low-angle surface pushes the ball
              outward. The ball, constrained to move only radially by the
              modulator slot, then loads the steep output wedge. The output can
              only respond by rotating — but slowly, because every increment of
              the input angle produces a much smaller increment at the output.
              That ratio is the reduction.
            </p>

            <div className="mt-8 grid grid-cols-2 gap-3">
              <div className="border border-white/10 p-4">
                <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">
                  Load distribution
                </div>
                <div className="mt-1 font-display font-bold text-xl">All balls, all the time</div>
              </div>
              <div className="border border-white/10 p-4">
                <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">
                  Backlash
                </div>
                <div className="mt-1 font-display font-bold text-xl">~0</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONFIGURATIONS */}
      <section className="relative py-24 md:py-32 bg-[#0a0a0a] border-y border-white/5" data-testid="tech-configs">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <SectionHeader
            kicker="/ configurations"
            title={<>Three core configurations.<br />One adjustable overlay.</>}
            lead="Every LogDrive ships in one of three architectural variants. Each can be combined with the Adjustable Back-Drive (ABD) overlay to tune holding torque, free-wheel behaviour, and back-drive characteristics in-system."
          />
          <div className="mt-14 grid md:grid-cols-3 gap-4">
            {[
              {
                tag: "SR",
                name: "Speed Reducer",
                desc: "Coaxial reduction stage. Single- or two-stage stack delivers 5:1 to 225:1 with 75 – 95% efficiency and near-zero backlash in a hollow-shaft envelope.",
                mode: "sr",
              },
              {
                tag: "MI",
                name: "Motion Inverter",
                desc: "Single rotary input splits into two coaxial, counter-rotating outputs in a single compact housing. No idler gears, no offset shafts.",
                mode: "mi",
              },
              {
                tag: "SR-MI",
                name: "Speed Reducer + Motion Inverter",
                desc: "Cascaded reduction and inversion in one coaxial unit — high reduction ratio with two counter-rotating outputs on a shared centerline.",
                mode: "sr-mi",
              },
            ].map((c) => (
              <div
                key={c.tag}
                className="border border-white/10 p-6 bg-black flex flex-col"
                data-testid={`config-${c.tag.toLowerCase()}`}
              >
                <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">
                  {c.tag}
                </div>
                <div className="mt-2 font-display font-bold text-2xl">{c.name}</div>
                <p className="mt-4 text-white/70 leading-relaxed">{c.desc}</p>
                <div className="mt-6 border border-white/10 bg-[#050505] p-3">
                  <ConfigurationSchematic mode={c.mode} />
                </div>
              </div>
            ))}
          </div>

          {/* ABD overlay row */}
          <div
            className="mt-6 border border-[#FF3B30]/30 bg-black p-6 md:p-8 grid md:grid-cols-12 gap-6 items-center"
            data-testid="config-abd"
          >
            <div className="md:col-span-2">
              <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[#FF3B30]">
                + Overlay
              </div>
              <div className="mt-2 font-display font-black text-3xl tracking-tighter">
                ABD
              </div>
            </div>
            <div className="md:col-span-7">
              <div className="font-display font-bold text-xl">
                Adjustable Back-Drive
              </div>
              <p className="mt-2 text-white/70 leading-relaxed">
                A modulator-side adjustment that sets the unit's back-drive
                threshold on demand — from fully self-locking, through
                controlled compliance, to free-wheeling. Compatible with all
                three core configurations (SR, MI, SR-MI) and tunable from the
                integrator's control system.
              </p>
            </div>
            <div className="md:col-span-3 grid grid-cols-3 gap-2">
              {[
                { k: "SR", c: "#FF3B30" },
                { k: "MI", c: "#F59E0B" },
                { k: "SR-MI", c: "#FFFFFF" },
              ].map((t) => (
                <div
                  key={t.k}
                  className="border border-white/10 p-3 text-center"
                  style={{ borderLeftColor: t.c, borderLeftWidth: 2 }}
                >
                  <div
                    className="font-mono-tech text-[9px] uppercase tracking-widest"
                    style={{ color: t.c }}
                  >
                    + ABD
                  </div>
                  <div className="mt-1 font-display font-bold text-sm">
                    {t.k}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PERMUTATIONS */}
      <section
        className="relative py-24 md:py-32 border-y border-white/5 bg-[#050505]"
        data-testid="tech-permutations"
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <SectionHeader
            kicker="/ permutations"
            title={<>Three discs.<br />Three fixed positions.</>}
            lead={
              <>
                A planetary drive has three active elements — the sun gear,
                planet carrier, and ring gear — and by choosing which one is
                fixed, driven, or taken as the output, the same hardware
                produces very different speed and torque ratios. LogDrive
                applies the same principle to{" "}
                <span className="text-white">Disc 3 (top)</span>,{" "}
                <span className="text-white">Disc 2 (middle)</span>, and{" "}
                <span className="text-white">Disc 1 (bottom)</span>. Each
                arrangement below shows a different disc held fixed; the
                remaining two trade input and output roles to deliver the
                listed direct and contra-rotation transmission and torque
                ratios.
              </>
            }
          />

          {/* Section-level legend (shown ONCE) */}
          <div className="mt-10 inline-flex flex-wrap gap-6 px-5 py-3 border border-white/10 bg-black font-mono-tech text-[10px] uppercase tracking-[0.22em] text-white/75">
            <span className="flex items-center gap-2">
              <span className="inline-block w-4 h-3" style={{ background: "#FF3B30" }} />
              Input
            </span>
            <span className="flex items-center gap-2">
              <span className="inline-block w-4 h-3" style={{ background: "#22D3EE" }} />
              Fixed
            </span>
            <span className="flex items-center gap-2">
              <span className="inline-block w-4 h-3" style={{ background: "#22C55E" }} />
              Output
            </span>
            <span className="text-white/40">
              Tr. Ratio = Speed In / Speed Out · Torque Ratio = Trq. In / Trq. Out
            </span>
          </div>

          <div className="mt-10 grid lg:grid-cols-3 gap-4">
            {[
              {
                title: "Disc 2 Fixed",
                variants: [
                  {
                    src: "/perms-svg/disc2-fixed-a.svg",
                    label: "Disc 1 → Disc 3",
                    direct: { tr: "3.5 : 1", trq: "0.2857" },
                    contra: { tr: "−3.5 : 1", trq: "0.2857" },
                  },
                  {
                    src: "/perms-svg/disc2-fixed-b.svg",
                    label: "Disc 3 → Disc 1",
                    direct: { tr: "0.2857 : 1", trq: "3.5" },
                    contra: { tr: "−0.2857 : 1", trq: "3.5" },
                  },
                ],
              },
              {
                title: "Disc 3 Fixed",
                variants: [
                  {
                    src: "/perms-svg/disc3-fixed-a.svg",
                    label: "Disc 1 → Disc 2",
                    direct: { tr: "−0.4 : 1", trq: "2.5" },
                    contra: { tr: "0.222 : 1", trq: "4.5" },
                  },
                  {
                    src: "/perms-svg/disc3-fixed-b.svg",
                    label: "Disc 2 → Disc 1",
                    direct: { tr: "−2.5 : 1", trq: "0.4" },
                    contra: { tr: "4.5 : 1", trq: "0.2222" },
                  },
                ],
              },
              {
                title: "Disc 1 Fixed",
                variants: [
                  {
                    src: "/perms-svg/disc1-fixed-a.svg",
                    label: "Disc 3 → Disc 2",
                    direct: { tr: "0.7143 : 1", trq: "1.4" },
                    contra: { tr: "1.2857 : 1", trq: "0.778" },
                  },
                  {
                    src: "/perms-svg/disc1-fixed-b.svg",
                    label: "Disc 2 → Disc 3",
                    direct: { tr: "1.4 : 1", trq: "0.7143" },
                    contra: { tr: "0.7778 : 1", trq: "1.2857" },
                  },
                ],
              },
            ].map((box) => (
              <div
                key={box.title}
                className="border border-white/10 bg-black flex flex-col"
                data-testid={`perm-box-${box.title.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <div className="px-5 py-3 border-b border-white/10">
                  <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/55">
                    {box.title}
                  </div>
                </div>
                <div className="divide-y divide-white/10">
                  {box.variants.map((v) => (
                    <div key={v.src} className="p-5">
                      <div className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-white/45 mb-3">
                        Input → Output · {v.label}
                      </div>
                      <object
                        type="image/svg+xml"
                        data={v.src}
                        aria-label={`${box.title} — ${v.label}`}
                        className="block w-full h-auto pointer-events-none"
                      />
                      <table className="mt-4 w-full text-left font-mono-tech text-[10px] uppercase tracking-widest">
                        <thead>
                          <tr className="text-white/40">
                            <th className="font-normal pb-1 pr-3"></th>
                            <th className="font-normal pb-1 pr-3">Tr. Ratio</th>
                            <th className="font-normal pb-1">Torque Ratio</th>
                          </tr>
                        </thead>
                        <tbody className="font-display text-sm text-white">
                          <tr className="border-t border-white/10">
                            <td className="py-2 pr-3 text-white/55 font-mono-tech text-[10px] tracking-widest">
                              Direct
                            </td>
                            <td className="py-2 pr-3">{v.direct.tr}</td>
                            <td className="py-2">{v.direct.trq}</td>
                          </tr>
                          <tr className="border-t border-white/10">
                            <td className="py-2 pr-3 text-white/55 font-mono-tech text-[10px] tracking-widest">
                              Contra
                            </td>
                            <td className="py-2 pr-3">{v.contra.tr}</td>
                            <td className="py-2">{v.contra.trq}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <p className="mt-10 text-white/55 text-sm leading-relaxed max-w-3xl">
            Together with the SR / MI / SR-MI configurations and the ABD
            overlay, a single LogDrive platform spans applications from
            low-speed actuators to high-RPM generators without re-tooling.
          </p>
        </div>
      </section>
    </main>
  );
}
